import { useTranslation } from 'react-i18next';
import { getCurrentUser } from '@features/auth';
import type { ApiResponse, HubspotAccount } from '@types';
import { microServicesRoutes } from '@constants';
import { syncApi } from '@lib/apiClients.ts';
import { logInfo } from '@lib/logger/logInfo.ts';
import { Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';

interface useHubspotApiProps {
    loginToHubspot: () => Promise<void>;
    getAllHubspotAccounts: (
        userId: string, organizationId: string
    )=> Promise<ApiResponse<HubspotAccount[]>>;
  removeHubspotAccount: (
      userId: string, organizationId: string, nonce: string
  )=> Promise<ApiResponse<void>>;
}

export function useHubspotApi (): useHubspotApiProps {
  const { t } = useTranslation();
    
  async function loginToHubspot (): Promise<void> {
    const currentUser = getCurrentUser();
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }
    const { userId, organizationId } = currentUser;
    const response = await linkHubspotAccount(userId, organizationId);

    if (response.data === undefined) {
      throw new Error('No data returned from link for Hubspot Account.');
    }
    const oAuthUrl = response.data.url;

    const popup = window.open(
      oAuthUrl,
      'hubspotPopup',
      `noopener,width=${500},height=${500}`
    );

    if (popup !== null) {
      popup.window.addEventListener('unload', ()=> {
        void logInfo(
          t('settings.integrationsPages.loadingMessage', { integrationName: Integrations.Hubspot }),
          'linkOutreachAccount',
          { component: useHubspotApi.name },
          true
        );
      });
    }
    
  }
  async function linkHubspotAccount (
    userId: string, organizationId: string
  ): Promise<ApiResponse<{url:string}>> {
    try {
      const url = `${microServicesRoutes.sync.post.hubspot.oAuthLogin}/${organizationId}/${userId}`;
      return await syncApi.post(url);
    } catch (e: unknown) {
      return { error: { message:
              'Error while linking hubspot account.',
      }, success: false };
    }
  }

  async function getAllHubspotAccounts (
    userId: string, organizationId: string
  ): Promise<ApiResponse<HubspotAccount[]>> {
    try {
      const url = `${microServicesRoutes.sync.get.hubspot.getLinkedAccounts}/${organizationId}/${userId}`;
      return await syncApi.get(url);
    } catch (e: unknown) {
      return { error: { message:
      'Error while getting hubspot account.' }, 
      success: false };
    }
  }
  
  async function removeHubspotAccount (
    organizationId: string, userId: string, nonce: string
  ): Promise<ApiResponse<void>> {
    try {
      const url = `${microServicesRoutes.sync.delete.hubspot.deleteLinkedAccount}/${organizationId}/${userId}/${nonce}}`;
      return await syncApi.delete(url);
    } catch (e: unknown) {
      return { error:{ message: 
            'Error while deleting hubspot account.' }, 
      success: false };
    }
  }


  return {
    loginToHubspot,
    getAllHubspotAccounts,
    removeHubspotAccount,
  };
}